/**
 * Hook for managing conversation participant actions.
 * Composes add participant and leave conversation functionality.
 */

import { useUser } from '@clerk/clerk-react'
import type { ConversationParticipants } from '@saaga/shared/interfaces/conversation.interface'

import type { SelectableMember } from '@/components/chat/add-participant-member-list.molecule'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

import { useAddParticipant } from './use-add-participant.hook'
import { useLeaveConversation } from './use-leave-conversation.hook'

interface UseChatParticipantActionsParams {
  conversationId: string | null
  participants: ConversationParticipants | undefined
  userLookup: UserLookupMap
}

export interface UseChatParticipantActionsReturn {
  isAddParticipantDialogOpen: boolean
  isLeaveDialogOpen: boolean
  availableMemberList: SelectableMember[]
  isMembersLoading: boolean
  isAddingParticipant: boolean
  isLeavingConversation: boolean
  openAddParticipantDialog: () => void
  closeAddParticipantDialog: () => void
  setAddParticipantDialogOpen: (isOpen: boolean) => void
  openLeaveDialog: () => void
  closeLeaveDialog: () => void
  setLeaveDialogOpen: (isOpen: boolean) => void
  handleAddParticipant: (userId: string) => Promise<void>
  handleLeaveConversation: () => Promise<void>
}

export const useChatParticipantActions = ({
  conversationId,
  participants,
  userLookup,
}: UseChatParticipantActionsParams): UseChatParticipantActionsReturn => {
  const { user } = useUser()

  const addParticipant = useAddParticipant({
    conversationId,
    participants,
    userLookup,
    currentUserId: user?.id,
  })

  const leaveConversation = useLeaveConversation({ conversationId })

  return {
    isAddParticipantDialogOpen: addParticipant.isDialogOpen,
    isLeaveDialogOpen: leaveConversation.isDialogOpen,
    availableMemberList: addParticipant.availableMemberList,
    isMembersLoading: addParticipant.isMembersLoading,
    isAddingParticipant: addParticipant.isAddingParticipant,
    isLeavingConversation: leaveConversation.isLeavingConversation,
    openAddParticipantDialog: addParticipant.openDialog,
    closeAddParticipantDialog: addParticipant.closeDialog,
    setAddParticipantDialogOpen: addParticipant.setDialogOpen,
    openLeaveDialog: leaveConversation.openDialog,
    closeLeaveDialog: leaveConversation.closeDialog,
    setLeaveDialogOpen: leaveConversation.setDialogOpen,
    handleAddParticipant: addParticipant.handleAddParticipant,
    handleLeaveConversation: leaveConversation.handleLeaveConversation,
  }
}
